import { useMutation } from '@tanstack/react-query';
import { Button } from '@givebutter/primitives/button';
import { Loader } from '@givebutter/primitives/loader';
import * as Popover from '@givebutter/primitives/popover';
import { Separator } from '@givebutter/primitives/separator';
import * as SingleSelect from '@givebutter/primitives/single-select';
import { useToast } from '@givebutter/primitives/toast';
import { faCog, faRefresh } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { triggerIntegrationActions, updateIntegration } from 'api/integrations';
import { BloomerangMappingItem } from 'assets/js/pages/account-settings/account-integrations/Bloomerang/BloomerangMappingItem';
import { BLOOMERANG_INTEGRATION_NOT_FOUND_ERROR } from 'assets/js/pages/account-settings/account-integrations/constants';
import * as Form from 'components/form';
import { useCurrentAccount } from 'customHooks/useCurrentAccount';
import { useCurrentAccountIntegrations } from 'customHooks/useCurrentAccountIntegrations';
import { useInfiniteCampaigns } from 'customHooks/useInfiniteCampaigns';
import { accountQueryKeys } from 'queries/account-query-keys';
import { queryClient } from 'queries/queryClient';
import { Integration } from 'types/domain';
import { GivebutterAxiosError } from 'types/error';

interface BloomerangMappingSettingsFormValues {
    DefaultAcknowledgementStatus: string;
    ZeroTransactionValueSync: string;
}

const BloomerangMapping = () => {
    const { data: integrations } = useCurrentAccountIntegrations();
    const { bloomerang } = integrations || {};

    const { success, error } = useToast();
    const { accountId } = useCurrentAccount();
    const {
        data: campaignData,
        ref: infiniteScrollRef,
        isFetchingNextPage,
    } = useInfiniteCampaigns();
    const campaigns = campaignData?.pages ?? [];

    const methods = Form.useGivebutterForm<BloomerangMappingSettingsFormValues>({
        defaultValues: {
            DefaultAcknowledgementStatus:
                bloomerang?.settings?.DefaultAcknowledgementStatus || 'No',
            ZeroTransactionValueSync: convertPossibleBooleanToString(
                bloomerang?.settings?.ZeroTransactionValueSync,
            ),
        },
    });
    const {
        control,
        formState: { isDirty },
        handleSubmit,
        path,
        reset,
    } = methods;

    const { mutate: refresh, isPending: isRefreshing } = useMutation<
        Integration,
        GivebutterAxiosError
    >({
        mutationFn: () => {
            if (!bloomerang?.id) {
                throw new Error(BLOOMERANG_INTEGRATION_NOT_FOUND_ERROR);
            }

            return triggerIntegrationActions(accountId, bloomerang.id, [
                'fund.sync',
                'campaign.sync',
                'appeal.sync',
            ]);
        },
        onSuccess: () => {
            success('Bloomerang Mapping refreshed successfully!');
        },
        onError: e => {
            error(e.response?.data?.message || 'Something went wrong. Please try again.');
        },
    });

    const { mutate: submit, isPending: isSubmitting } = useMutation<
        Integration,
        GivebutterAxiosError,
        BloomerangMappingSettingsFormValues
    >({
        mutationFn: values => {
            if (!bloomerang?.id) {
                throw new Error(BLOOMERANG_INTEGRATION_NOT_FOUND_ERROR);
            }

            return updateIntegration(accountId, bloomerang.id, {
                settings: {
                    ZeroTransactionValueSync: values.ZeroTransactionValueSync === 'true',
                    DefaultAcknowledgementStatus: values.DefaultAcknowledgementStatus,
                },
            });
        },
        onSuccess: (_response, values) => {
            queryClient.invalidateQueries({
                queryKey: accountQueryKeys.integrations(accountId),
            });
            success('Bloomerang Sync settings saved successfully!');
            reset(values);
        },
        onError: e => {
            error(e.response?.data?.message || 'Something went wrong. Please try again.');
        },
    });

    return (
        <>
            <p className="text-sm font-medium">Campaign Mapping</p>
            <div className="flex flex-col gap-4 md:flex-row md:items-center">
                <p className="flex-shrink text-sm">
                    Map your Givebutter campaigns to the appropriate Funds, Campaigns, and Appeals
                    in Bloomerang.
                </p>
                <div className="flex gap-4">
                    <Popover.Root>
                        <Popover.Trigger asChild>
                            <Button variant="outline" color="neutral" size="sm">
                                <FontAwesomeIcon icon={faCog} />
                                Settings
                            </Button>
                        </Popover.Trigger>
                        <Popover.Portal>
                            <Popover.Content
                                className="max-w-xs p-4"
                                side="bottom"
                                align="end"
                                sideOffset={5}
                            >
                                <Form.Provider {...methods}>
                                    <form
                                        onSubmit={handleSubmit(values => submit(values))}
                                        className="flex flex-col items-start gap-4"
                                    >
                                        <Form.Container>
                                            <Form.Label name={path('DefaultAcknowledgementStatus')}>
                                                Default Acknowledgement Status
                                            </Form.Label>
                                            <p className="text-xs">
                                                Change the default acknowledgment status when
                                                transactions are synced into Bloomerang.
                                            </p>
                                            <Form.SingleSelect
                                                control={control}
                                                name="DefaultAcknowledgementStatus"
                                            >
                                                <SingleSelect.Trigger aria-label="Default acknowledgement status" />
                                                <SingleSelect.Content>
                                                    <SingleSelect.Item value="Yes">
                                                        <SingleSelect.ItemText>
                                                            Yes
                                                        </SingleSelect.ItemText>
                                                    </SingleSelect.Item>
                                                    <SingleSelect.Item value="No">
                                                        <SingleSelect.ItemText>
                                                            No
                                                        </SingleSelect.ItemText>
                                                    </SingleSelect.Item>
                                                </SingleSelect.Content>
                                            </Form.SingleSelect>
                                        </Form.Container>
                                        <Separator />
                                        <Form.Container>
                                            <Form.Label name={path('ZeroTransactionValueSync')}>
                                                Sync Zero Value Transactions
                                            </Form.Label>
                                            <p className="text-xs">
                                                Change the default acknowledgment status when
                                                transactions are synced into Bloomerang.
                                            </p>
                                            <Form.SingleSelect
                                                control={control}
                                                name="ZeroTransactionValueSync"
                                            >
                                                <SingleSelect.Trigger aria-label="Sync $0 transactions" />
                                                <SingleSelect.Content>
                                                    <SingleSelect.Item value="true">
                                                        <SingleSelect.ItemText>
                                                            Sync $0 transactions
                                                        </SingleSelect.ItemText>
                                                    </SingleSelect.Item>
                                                    <SingleSelect.Item value="false">
                                                        <SingleSelect.ItemText>
                                                            Don’t Sync $0 Transactions
                                                        </SingleSelect.ItemText>
                                                    </SingleSelect.Item>
                                                </SingleSelect.Content>
                                            </Form.SingleSelect>
                                        </Form.Container>
                                        <Form.SubmitButton
                                            isSubmitting={isSubmitting}
                                            disabled={!isDirty}
                                        >
                                            Save
                                        </Form.SubmitButton>
                                    </form>
                                </Form.Provider>
                            </Popover.Content>
                        </Popover.Portal>
                    </Popover.Root>

                    <Button
                        variant="outline"
                        color="neutral"
                        size="sm"
                        onClick={() => refresh()}
                        disabled={isRefreshing}
                    >
                        <FontAwesomeIcon
                            icon={faRefresh}
                            className={isRefreshing ? 'animate-spin' : ''}
                        />
                        Refresh
                    </Button>
                </div>
            </div>
            {bloomerang ? (
                <div className="flex flex-col gap-2">
                    {campaigns.map(campaign => (
                        <BloomerangMappingItem key={campaign.id} campaign={campaign} />
                    ))}
                    <div ref={infiniteScrollRef} className="h-4" />
                    {isFetchingNextPage ? (
                        <div className="flex items-center justify-center p-4">
                            <Loader variant={'static'} />
                        </div>
                    ) : null}
                </div>
            ) : null}
        </>
    );
};

const convertPossibleBooleanToString = (value: null | undefined | string | boolean) => {
    if (value === null || typeof value === 'undefined') {
        return 'false';
    }

    if (typeof value === 'boolean') {
        return String(value);
    }

    return value;
};

export { BloomerangMapping };
